import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/common/kit";
import { ProductSelect } from "@/components/common/Filters";

export default function SRequests({ kind }) {
  const { salesman } = useAuth();
  const isT = kind === "transfers";
  const [rk, setRk] = useState(0);
  const [f, setF] = useState({ product_id: "", qty: "", to_salesman_id: "", reason: "", remarks: "" });
  const { data: stock } = useFetch("/stock/current", { salesman_id: salesman.id }, rk);
  const { data: dir } = useFetch(isT ? "/salesmen/directory" : null);
  const { data: list } = useFetch(`/${kind}`, { limit: 30 }, rk);
  const items = (stock?.items || []).filter((s) => s.qty > 0);
  const sel = items.find((s) => s.product_id === f.product_id);

  const submit = async () => {
    try {
      const body = isT ? { to_salesman_id: f.to_salesman_id, product_id: f.product_id, qty: Number(f.qty), remarks: f.remarks }
        : { product_id: f.product_id, qty: Number(f.qty), reason: f.reason, remarks: f.remarks };
      await api.post(`/${kind}`, body);
      toast.success("Request sent for admin approval");
      setF({ product_id: "", qty: "", to_salesman_id: "", reason: "", remarks: "" });
      setRk(rk + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold tracking-tight">{isT ? "Stock Transfers" : "Stock Returns"}</h1>
      <div className="space-y-3 rounded-md border border-slate-200 bg-white p-4" data-testid={`${kind}-request-form`}>
        <p className="text-xs text-slate-500">{isT ? "Transfer to another salesman. Stock moves only after admin approval." : "Return stock to company. Removed from your stock after admin approval."}</p>
        <ProductSelect value={f.product_id} onChange={(v) => setF({ ...f, product_id: v })} products={items} testid={`${kind}-product`} />
        {sel && <p className="text-xs text-slate-600">Available to {isT ? "transfer" : "return"}: <b>{sel.movable}</b> packs</p>}
        {isT && (
          <Select value={f.to_salesman_id || undefined} onValueChange={(v) => setF({ ...f, to_salesman_id: v })}>
            <SelectTrigger className="h-10" data-testid="transfer-to-select"><SelectValue placeholder="Transfer to salesman" /></SelectTrigger>
            <SelectContent>{(dir || []).map((s) => <SelectItem key={s.id} value={s.id} data-testid={`transfer-to-${s.code}`}>{s.full_name} ({s.code})</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Input type="number" inputMode="numeric" placeholder="Quantity (packs)" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} className="h-12 font-mono text-lg" data-testid={`${kind}-qty`} />
        {!isT && <Input placeholder="Reason (e.g. damaged, excess)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="h-11" data-testid="return-reason" />}
        <Input placeholder="Remarks (optional)" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} className="h-11" data-testid={`${kind}-remarks`} />
        <Button className="h-12 w-full" disabled={!f.product_id || !f.qty || (isT && !f.to_salesman_id) || (!isT && !f.reason)} onClick={submit} data-testid={`${kind}-submit-button`}>Send request</Button>
      </div>
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">My requests</p>
        {(list?.items || []).map((r) => (
          <div key={r.id} className="rounded-md border border-slate-200 bg-white px-4 py-3" data-testid={`my-${kind}-${r.transfer_no || r.return_no}`}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-sm font-semibold">{r.transfer_no || r.return_no}</p><StatusBadge status={r.status} />
            </div>
            <p className="mt-1 text-sm">{r.qty} pks · {r.product_name}</p>
            <p className="text-xs text-slate-500">
              {isT ? (r.from_salesman_id === salesman.id ? `To ${r.to_name}` : `From ${r.from_name}`) : r.reason} · {fmtDate(r.request_date)}
              {r.decision_remarks && ` · Admin: ${r.decision_remarks}`}
            </p>
          </div>
        ))}
        {list?.items?.length === 0 && <p className="text-sm text-slate-500">No requests yet.</p>}
      </div>
    </div>
  );
}
